import { useEffect, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import styles from './AppointmentRoot.module.scss';
import { PageRootLanding } from '../../ui/components/PageBuilder/PageRootLanding/PageRootLanding';
import { Heading } from '../../ui/components/Heading/Heading';
import { StepIndicator } from '../../ui/components/Steps/StepIndicator/StepIndicator';
import { StateText } from '../../ui/components/StateText/StateText';
import { Button } from '../../ui/components/Button/Button';
import { Input } from '../../ui/components/Input/Input';
import { useGQLMutation } from '../../ui/utils/graphql';
import { CHECK_CLIENT } from '../../graphql/checkClient';
import { CodeInput } from '../../ui/components/CodeInput/CodeInput';
import { Clock } from '../../ui/components/Clock/Clock';
import { VERIFY_CODE } from '../../graphql/verifyCode';
import { SetAppointmentType } from './components/SetAppointmentType/SetAppointmentType';

interface CheckResponse {
  validCaptcha: boolean;
  codeCreatedAt: string;
}

interface VerifyCodeRequest {
  email: string;
  createdAt: string;
  code: string;
}

interface VerifyCodeResponse {
  token: string;
  verified: boolean;
}

export const AppointmentRoot = () => {
  const [step, setStep] = useState<number>(1);
  const titles = ['Before start', 'Get a code', 'Set the code'];
  const [email, setEmail] = useState<string>();
  const [errorMessage] = useState<string>(null);
  const [captchaToken, setCaptchaToken] = useState<string>(null);
  const [code, setCode] = useState('');
  const { mutation, data: checkResponse } = useGQLMutation<CheckResponse, any>(
    CHECK_CLIENT,
    {
      onCompleted: (d) => {
        if (d.validCaptcha) {
          setStep(3);
        }
      },
    }
  );

  const { mutation: verifyCodeMutation } = useGQLMutation<
    VerifyCodeResponse,
    VerifyCodeRequest
  >(VERIFY_CODE, {
    onCompleted: (d) => {
      if (d.verified) {
        setStep(4);
      }
    },
  });

  // const { mutation: validateCode } = useGQLMutation<CheckResponse, any>(
  //   CHECK_CLIENT,
  //   {
  //     onCompleted: (d) => {
  //       if (d.validCaptcha) {
  //         setStep(3);
  //       }
  //       setCheckResponse(d);
  //     },
  //   }
  // );

  const beforeStartJSX = (
    <>
      <StateText className={styles['number-info']}>
        You will need your Mexican phone number with 10 digits to send you a
        message with a code
      </StateText>
      <StateText className={styles['number-info']}>
        For functionality of the service we will retain your personal
        information to track your appointement and make easier scheduling your
        appointment next time
      </StateText>
      <StateText className={styles['number-info']}>
        Using the service you accept how we treat your information
      </StateText>
      <Button className={styles.button} onClick={() => setStep(2)}>
        Continue
      </Button>
    </>
  );

  const getACodeJSX = (
    <div className={styles['get-code']}>
      <Input
        onChange={(event) => {
          setEmail(event?.target?.value);
        }}
        label="Email"
        placeholder="Example: john@gmail.com"
        classname={styles.input}
        errorMessage={errorMessage}
      />
      <div className={styles.recaptcha}>
        <ReCAPTCHA
          onChange={(event) => {
            setCaptchaToken(event);
          }}
          sitekey="6LdDEE8qAAAAAD1Pa6mjuHpcI566jiGSXlHTTMPj"
        />
      </div>
      <Button
        className={styles.button}
        isDisabled={!captchaToken}
        onClick={() => {
          mutation({
            variables: {
              captcha: captchaToken,
              email,
            },
          });
        }}
      >
        Get a code
      </Button>
    </div>
  );

  const setTheCodeJSX = (
    <div className={styles['set-code']}>
      <StateText className={styles.info} type="info">
        Please enter the code we send to your mail box associated with your
        email: {email}
      </StateText>
      <div className={styles['timer-advice']}>
        <Clock
          className={styles.timer}
          tool="timer"
          timerConfiguration={{
            limit: '00:05:00',
            startFromLimit: true,
            initTime: checkResponse?.codeCreatedAt,
          }}
          configuration={{ format: 'hh:mm:ss' }}
        />
        <p className={styles.message}>Time remaining to enter the code</p>
      </div>
      <CodeInput
        size={6}
        onChange={(c) => {
          setCode(c);
        }}
      />
      <Button
        className={styles.button}
        isDisabled={code.length < 6}
        onClick={() => {
          verifyCodeMutation({
            variables: {
              code,
              email,
              createdAt: checkResponse.codeCreatedAt,
            },
          });
        }}
      >
        Review your code
      </Button>
    </div>
  );

  const stepsJSX = [
    <SetAppointmentType key="AppointmentType" />,
    beforeStartJSX,
    getACodeJSX,
    setTheCodeJSX,
  ];

  useEffect(() => {}, [email]);

  return (
    <PageRootLanding colorPalettes="doctor-1" title="Book an appointment">
      <div className={styles.root}>
        <Heading className={styles.heading} headingLevel="h2">
          {titles[step - 1]}
        </Heading>
        <StepIndicator
          color="excellent"
          numberSteps={4}
          actualStep={step}
          stepStyle="rectangle"
        />
        <div>{stepsJSX[step - 1]}</div>
      </div>
    </PageRootLanding>
  );
};
